import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import { LINKS } from '../../lib/links'
import { useT } from '../../i18n/LocaleContext'
import { localizedPath } from '../../i18n/routes'

type Props = {
  badge: string
  h1Lead: string
  h1Highlight: string
  h1Tail: string
  subhead: string
  ctaPrimary: string
  reassure: string
  breadcrumbLabel: string
  /** Visuel signature de la page (animation hero). Optionnel — sinon hero centré. */
  visual?: ReactNode
}

/**
 * Hero partagé des pages Expertise (ADR / AFSCA).
 * Above-the-fold : reveals immédiats (`animate`), pas de `whileInView`.
 */
export default function ExpertiseHero({
  badge,
  h1Lead,
  h1Highlight,
  h1Tail,
  subhead,
  ctaPrimary,
  reassure,
  breadcrumbLabel,
  visual,
}: Props) {
  const { t, locale } = useT()
  const hasVisual = Boolean(visual)

  return (
    <section className="relative max-w-6xl mx-auto px-5 sm:px-6">
      <div
        className={`grid items-center gap-10 lg:gap-14 ${
          hasVisual ? 'lg:grid-cols-2' : 'max-w-3xl'
        }`}
      >
        {/* ── Texte ── */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-xs sm:text-sm text-ink-500"
          >
            <Link
              to={localizedPath('home', locale)}
              className="hover:text-accent-700 transition-colors"
            >
              {t('breadcrumb.home')}
            </Link>
            <span className="mx-2 text-ink-300">/</span>
            <span className="text-ink-700">{breadcrumbLabel}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 sm:mt-8 inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
            </span>
            <span className="font-medium">{badge}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="mt-5 sm:mt-6 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(2.3rem,5.5vw,4rem)] leading-[1.05]"
          >
            {h1Lead}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.14em] pr-[0.06em] inline-block">
              {h1Highlight}
            </span>
            {h1Tail}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-5 sm:mt-6 text-base sm:text-lg text-ink-600 leading-relaxed max-w-2xl"
          >
            {subhead}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3"
          >
            <Link
              to={`${localizedPath('contact', locale)}#rendez-vous`}
              className="relative group inline-flex items-center gap-2 bg-accent-700 hover:bg-accent-800 text-white rounded-full pl-5 pr-4 py-3 text-sm font-medium shadow-[0_16px_50px_-12px_rgba(59,93,255,0.65)] transition-colors overflow-hidden"
            >
              <span
                aria-hidden
                className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
              />
              <span className="relative">{ctaPrimary}</span>
              <span className="relative flex items-center justify-center w-5 h-5 rounded-full bg-white/20">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </Link>
            <span className="inline-flex items-center gap-2 text-xs sm:text-sm text-ink-500">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {reassure}
            </span>
          </motion.div>
        </div>

        {/* ── Visuel signature ── */}
        {hasVisual && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="relative"
          >
            {visual}
          </motion.div>
        )}
      </div>
    </section>
  )
}
